/**
 * 저장된 게시판 본문 **HTML** 을 그린다 (읽기 화면).
 *
 * ⛔ **옛 글(`contentType` 이 `'TEXT'` 이거나 없음)에는 쓰지 말 것.** 부르는 쪽이
 *   `contentType` 으로 **갈라서** 써야 한다 — 한쪽으로 몰면 한쪽이 깨진다.
 *   (2026-09-23 감사: 운영에 살아 있는 글 3건 · **HTML 글 0건** — 옛 글이 «0건이 아니다».)
 *
 * ## 두 겹 sanitize 중 «앞 겹이 아니라 뒷 겹»이다
 *
 * 서버가 이미 허용목록으로 거른다(create · PATCH). 그래도 여기서 한 번 더 `sanitizeBoardHtml`
 * 을 거친다 — 서버 sanitize «전»에 저장된 행이 있을 수 있고(감사로는 0건이지만 0 은
 * 「볼 대상이 없었다」였다), 한쪽이 우회돼도 다른 쪽이 막는다. 메일·전자결재가 쓰는 방식과 같다.
 *
 * ## 그림 — 저장된 `src` 를 **그대로 쓴다**
 *
 * 저장 `src` 는 `/api/v1/files/<id>/image` 이고 **서버에 그 라우트가 있다**
 * (`modules/board/board-image.routes.ts` · `board.routes.ts` 의 `registerBoardImageRoutes`).
 * 그 라우트는 `ETag` 를 주고 **`If-None-Match` 를 보아 304 를 낸다** — 한 글에 그림이 여럿이면
 * 그 값이 크다.
 *
 * ⚠ **처음에 나는 그 라우트가 «없다»고 적고 presigned 로 우회했다. 틀렸다.**
 *   `modules/files/` 만 grep 했기 때문이다 — 그 라우트는 **`modules/board/` 에 있고 «경로»만
 *   `/api/v1/files/...`** 다. **경로 앞머리와 모듈 폴더가 다르다.** 「한 폴더만 grep 했다」의
 *   한 얼굴이다. ⇒ 경로를 찾을 때는 **폴더를 가정하지 말고 경로 문자열로 전수 grep** 한다.
 *
 * ⇒ 그래서 **presigned 배관이 없다.** 렌더마다 발급을 받는 왕복도, 만료(TTL 3600)도 없고,
 *   **모바일·메일도 저장 HTML 을 그대로 그릴 수 있다**(주소가 만료 없는 앱 상대 주소다).
 *
 * ★ `data-file-id` 는 **그대로 둔다.** 서버 sanitize 프로필이 그 칸을 살려 두는 까닭이
 *   「그림 주소를 다시 만들 수 있게」이고, 그것이 저장·재편집의 정본 참조다.
 *
 * ## ⛔ 못 받은 그림은 «보이게» 한다
 *
 * 404·403 이든 **조용히 빈 자리**가 되면 안 된다 — 사용자가 무엇을 잃었는지 모른다.
 * `error` 를 받아 표시를 남긴다(`data-axi-image-failed`, CSS 가 그린다).
 */

import { useEffect, useRef } from 'react';
import { sanitizeBoardHtmlRich } from './axi/sanitize-board-rich';
import { bindYoutubeLite } from './axi/youtube-lite';
import './board-content.css';
import './axi/axi-content.css';

export interface BoardContentHtmlProps {
  /** 서버가 준 본문. `contentType === 'HTML'` 일 때만 넘긴다. */
  html: string;
  /** 바깥에서 여백·폭을 맞추려고 덧붙이는 클래스. */
  className?: string;
}

/** 못 받은 그림에 표시를 남긴다 — 빈 자리로 두지 않는다. */
function markFailed(img: HTMLImageElement): void {
  img.dataset.axiImageFailed = 'true';
  if (!img.alt) img.alt = '이 그림을 불러오지 못했습니다.';
}

export function BoardContentHtml({ html, className }: BoardContentHtmlProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    /*
     * 2026-10-14 — AxiEditor 도입으로 «리치» 판(`sanitizeBoardHtmlRich`)으로 거른다. 9-23 판
     * (`sanitizeBoardHtml`)의 상위집합이라 style·svg·영상이 없는 옛 글은 결과가 같다
     * (`axi/sanitize-board-rich.test.ts` 가 옛 표본으로 단언한다).
     */
    host.innerHTML = sanitizeBoardHtmlRich(html);
    const offYoutube = bindYoutubeLite(host);

    // 그림은 브라우저가 저장된 주소로 바로 받는다. 못 받은 것만 표시를 남긴다.
    const imgs = Array.from(host.querySelectorAll<HTMLImageElement>('img'));
    const off: Array<() => void> = [];
    for (const img of imgs) {
      // 이미 실패한 채로 붙었을 수 있다(캐시된 실패 등) — `error` 가 다시 안 온다.
      if (img.complete && img.naturalWidth === 0) { markFailed(img); continue; }
      const onError = () => markFailed(img);
      img.addEventListener('error', onError);
      off.push(() => img.removeEventListener('error', onError));
    }
    return () => { offYoutube(); for (const f of off) f(); };
  }, [html]);

  return (
    <div
      ref={hostRef}
      // ★ `axi-board-listmarks` — 목록 기호를 되살린 판의 표식(board-content.css 의 「목록 기호」 절).
      //   여기가 담는 것은 **본문 HTML 뿐**이다(첨부·댓글은 읽기 화면의 다른 탭) — 그래서 기호
      //   되살리기가 게시판 UI 목록에 번지지 않는다.
      // ★ `axi-board-content--axe` — AxiEditor 본문의 배치를 이 상자 안에 가둔다(axi/axi-content.css).
      className={`axi-board-content axi-board-listmarks axi-board-content--axe${className ? ` ${className}` : ''}`}
      // 내용은 위 effect 가 넣는다 — sanitize 와 그림 실패 표시를 «한 곳»에서 하려는 것이다.
    />
  );
}
